<template>
    <chart
        type="line"
        :chart-data="chartData"
        :tooltips="tooltips"
        :display-legend="displayLegend"
        :animate="animate"
        :options="options"
        :config="config"
        :datasets="datasets"
        :labels="labels"
    />
</template>

<script>
import Chart from 'src/components/charts/Chart';
import set from 'lodash/set';

export default {
    props: {
        tooltips: {
            type: Boolean,
            default: true,
        },
        displayY: {
            type: Boolean,
            default: true,
        },
        displayX: {
            type: Boolean,
            default: true,
        },
        displayYGrid: {
            type: Boolean,
            default: true,
        },
        displayXGrid: {
            type: Boolean,
            default: true,
        },
        displayLegend: {
            type: Boolean,
            default: true,
        },
        suggestedYMax: {
            type: Number,
            default: null,
        },
        borderColor: {
            type: String,
            default: null,
        },
        backgroundColor: {
            type: String,
            default: null,
        },
        chartData: {
            type: Array,
            default: () => [],
        },
        annotation: {
            type: Object,
            default: () => ({}),
        },
        animate: {
            type: Boolean,
            default: null,
        },
        plugins: {
            type: Array,
            default: () => [],
        },
    },
    components: {
        Chart,
    },
    computed: {
        labels() {
            return this.chartData.map(item => item.y);
        },
        config() {
            return {
                plugins: [
                    {
                        beforeInit: chart => {
                            if (chart.options.plugins.mode !== 'print') {
                                return;
                            }

                            chart.chart.options.elements.point.borderColor =
                                '#000';

                            chart.chart.options.scales.xAxes[0].ticks.fontColor =
                                '#000';
                            chart.chart.options.scales.yAxes[0].ticks.fontColor =
                                '#000';
                        },
                    },
                    {
                        beforeInit: chart => {
                            const printing =
                                chart.options.plugins.mode === 'print';

                            const gradients = this.getGradients(printing);
                            const borderColors = this.getBorderColors(printing);

                            chart.config.data.datasets = chart.config.data.datasets.map(
                                (set, index) => {
                                    set.backgroundColor = this.getBackgroundColor(
                                        chart.options.plugins.$el,
                                        chart.ctx,
                                        gradients[index],
                                    );

                                    set.borderColor = borderColors[index];

                                    return set;
                                },
                            );
                        },
                    },
                    ...this.plugins,
                ],
            };
        },
        options() {
            return {
                elements: {
                    point: {
                        radius: 0,
                        backgroundColor: this.backgroundColor,
                        borderColor: '#fff',
                        borderWidth: 1,
                    },
                },
                scales: {
                    yAxes: [
                        {
                            display: this.displayY,
                            gridLines: {
                                display: this.displayYGrid,
                                color: '#4d4d4d',
                                zeroLineColor: '#909090',
                            },
                            ticks: {
                                beginAtZero: true,
                                fontColor: '#fff',
                                suggestedMax: this.suggestedYMax,
                            },
                        },
                    ],
                    xAxes: [
                        {
                            display: this.displayX,
                            gridLines: {
                                display: this.displayXGrid,
                                color: '#4d4d4d',
                            },
                            ticks: {
                                fontColor: '#fff',
                            },
                        },
                    ],
                },
                annotation: this.annotation,
                ...(this.tooltips
                    ? {}
                    : {
                          events: [],
                      }),
            };
        },
        datasets() {
            return this.chartData
                .map(item => item.x)
                .reduce((prev, cur) => {
                    cur.map((item, key) => {
                        if (prev.length === key) {
                            prev.push([]);
                        }

                        prev[key].push(item);
                    });
                    return prev;
                }, [])
                .map((set, index) => ({
                    lineTension: 0,
                    label: set[0].label,
                    data: set.map(item => item.value),
                    borderWidth: 1,
                }));
        },
    },
    methods: {
        getGradients(printing) {
            const base = [
                {
                    start: 'rgba(76, 175, 80, .2)',
                    end: 'rgba(76, 175, 80, 0)',
                },
            ];

            if (printing) {
                return base.concat([
                    {
                        start: 'rgba(0, 0, 0, .2)',
                        end: 'rgba(0, 0, 0, 0)',
                    },
                ]);
            }

            return base.concat([
                {
                    start: 'rgba(255, 255, 255, .2)',
                    end: 'rgba(255, 255, 255, 0)',
                },
            ]);
        },
        getBorderColors(printing) {
            if (printing) {
                return ['rgba(76, 175, 80, 1)', 'rgba(0, 0, 0, .7)'];
            }

            return ['rgba(76, 175, 80, 1)', 'rgba(255, 255, 255, .7)'];
        },
        getBackgroundColor($el, ctx, gradient) {
            const height = $el.getBoundingClientRect().height;

            const stroke = ctx.createLinearGradient(0, 0, 0, height);

            stroke.addColorStop(0, gradient.start);
            stroke.addColorStop(1, gradient.end);

            return stroke;
        },
    },
};
</script>

